/**
 * Nest the flat taxonomy node list from `GET /v2/taxonomy/nodes` (linked by
 * `parent_uuid`) into a tree, bottom up: each node is attached to its parent. Nodes
 * stay identified by `uuid`; the backend `key` is the node's slug, which nothing
 * matches collection objects on (see "Object identity" in `@utils/api/collections`),
 * so it is left alone. Nodes whose parent
 * is missing from the list are treated as roots rather than dropped, and so is
 * any node whose parent chain loops back to itself — attaching it would make the
 * tree cyclic, and every recursive walk over it (render, search) would never end.
 */
export const buildObjectTree = (nodes = []) => {
	const byUuid = new Map(nodes.map(node => [node.uuid, { ...node, children: [] }]));
	const parentOf = node => (node.parent_uuid ? byUuid.get(node.parent_uuid) : undefined);

	// Terminates: each step either stops or adds a new node to `visited`, so it
	// takes at most one step per node.
	const isInCycle = start => {
		const visited = new Set();
		for (let current = parentOf(start); current; current = parentOf(current)) {
			if (current === start) return true;
			if (visited.has(current)) return false; // a loop above `start`, not through it
			visited.add(current);
		}
		return false;
	};

	const roots = [];
	byUuid.forEach(node => {
		const parent = parentOf(node);
		if (parent && !isInCycle(node)) parent.children.push(node);
		else roots.push(node);
	});
	return roots;
};

/** Whether a node is a Biodrone-maintained (read-only) system object. */
export const isSystemNode = node => node?.scope === 'system';

/**
 * The aliases a node shows — only system nodes display theirs. Search matches
 * these same aliases, so a result always shows why it matched.
 */
export const displayedAliases = node => (isSystemNode(node) ? (node.aliases ?? []) : []);

const normalize = value => value.toLowerCase().replace(/_/g, ' ');

const matchesQuery = (node, query) =>
	normalize(node.name ?? '').includes(query) ||
	displayedAliases(node).some(alias => normalize(alias).includes(query));

/**
 * Filter a tree from `buildObjectTree` to the nodes whose name (or, on a system
 * node, an alias) matches `query`, keeping each match's ancestors (so it stays in
 * context) and all of its descendants. A blank query returns the tree as is.
 */
export const filterObjectTree = (tree = [], query = '') => {
	const normalizedQuery = normalize(query.trim());
	if (!normalizedQuery) return tree;

	const filter = nodes =>
		nodes.reduce((result, node) => {
			if (matchesQuery(node, normalizedQuery)) {
				result.push(node);
				return result;
			}
			const children = filter(node.children ?? []);
			if (children.length > 0) result.push({ ...node, children });
			return result;
		}, []);

	return filter(tree);
};

/** Every uuid in a (possibly filtered) tree, for expand-all. */
export const collectUuids = (tree = []) =>
	tree.flatMap(node => [node.uuid, ...collectUuids(node.children ?? [])]);
